import { Component, OnInit, inject, effect, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { EstablishmentsService, ReplaceHoursPayload, DayDto } from '../../core/services/establishments.service';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';
import { SessionService } from '../../core/session.service';

type TimeSlot = { open: string; close: string };

@Component({
  selector: 'app-horaires',
  standalone: true,
  imports: [CommonModule, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './horaires.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./horaires.component.scss']
})
export class HorairesComponent implements OnInit {
  private api = inject(EstablishmentsService);
  private session = inject(SessionService);

  public schedules: TimeSlot[][] = [[], [], [], [], [], [], []];
  public daysEnabled: boolean[] = [true, true, true, true, true, true, false];

  private hoursInitial: DayDto[] = [];
  private id: string | null = null;

  // Toast
  toastVisible = false;
  toastSubtitle = '';
  private toastTimer: ReturnType<typeof setTimeout> | undefined;

  private selectedEffect = effect(
    () => {
      const selId = this.session.selectedId();
      if (selId && this.id !== selId) {
        this.id = selId;
        this.loadHours();
      }
    },
    { allowSignalWrites: true },
  );

  ngOnInit(): void {
    // L'effect se charge du chargement initial
  }

  private showToast(subtitle: string) {
    this.toastSubtitle = subtitle;
    this.toastVisible = true;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastVisible = false), 2200);
  }

  private loadHours(): void {
    const estId = this.session.selectedId();
    if (!estId) return;

    this.api.getDetails(estId).subscribe({
      next: (dto) => {
        this.hydrateHoursFromDto(dto.opening_hours ?? []);
        this.hoursInitial = this.normalizeHours(dto.opening_hours ?? []);
      },
      error: () => this.showToast('Erreur lors du chargement des horaires')
    });
  }

  private hydrateHoursFromDto(days: DayDto[]): void {
    this.daysEnabled = [false, false, false, false, false, false, false];
    this.schedules = [[], [], [], [], [], [], []];
    for (const d of days || []) {
      const idx = (Number(d.weekday) || 1) - 1;
      if (idx < 0 || idx > 6) continue;
      const slots = (d.slots || [])
        .map((s) => ({ open: this.toHHmm(s.start), close: this.toHHmm(s.end) }))
        .filter((s) => s.open && s.close)
        .sort((a, b) => a.open.localeCompare(b.open));
      this.daysEnabled[idx] = slots.length > 0;
      this.schedules[idx] = slots;
    }
  }

  private normalizeHours(days: DayDto[]): DayDto[] {
    const norm: DayDto[] = [];
    for (const d of days || []) {
      const wd = Number(d.weekday) || 1;
      const slots = (d.slots || [])
        .map((s) => ({ start: this.toHHmm(s.start), end: this.toHHmm(s.end) }))
        .filter((s) => s.start && s.end);
      if (slots.length) norm.push({ weekday: wd, slots });
    }
    norm.sort((a, b) => a.weekday - b.weekday);
    return norm;
  }

  private shallowEqual(a: DayDto[], b: DayDto[]): boolean {
    if (a.length !== b.length) return false;
    
    for (let i = 0; i < a.length; i++) {
      const dayA = a[i];
      const dayB = b[i];
      
      if (dayA.weekday !== dayB.weekday) return false;
      if (dayA.slots.length !== dayB.slots.length) return false;
      
      for (let j = 0; j < dayA.slots.length; j++) {
        const slotA = dayA.slots[j];
        const slotB = dayB.slots[j];
        if (slotA.start !== slotB.start || slotA.end !== slotB.end) return false;
      }
    }
    
    return true;
  }

  private canSaveHours(): boolean {
    for (let i = 0; i < 7; i++) {
      if (!this.daysEnabled[i]) continue;
      const slots = this.schedules[i] || [];
      for (let j = 0; j < slots.length; j++) {
        if (this.isSlotInvalid(i, j)) return false;
      }
    }
    const cur = this.normalizeHours(this.buildHoursPayload());
    return !this.shallowEqual(cur, this.hoursInitial);
  }

  onToggleDay(index: number, checked: boolean): void {
    this.daysEnabled[index] = checked;
    if (checked) {
      const arr = this.schedules[index] ?? [];
      if (!arr.length) {
        this.schedules[index] = [{ open: '07:30', close: '12:30' }];
      }
    }
  }

  addSlot(dayIndex: number): void {
    (this.schedules[dayIndex] ??= []).push({ open: '14:00', close: '19:30' });
  }

  removeLastSlot(dayIndex: number): void {
    const arr = this.schedules[dayIndex];
    if (arr?.length) arr.pop();
  }

  onTimeChange(dayIndex: number, slotIndex: number, field: 'open' | 'close', value: string): void {
    const slot = this.schedules[dayIndex]?.[slotIndex];
    if (slot) {
      slot[field] = this.toHHmm(value);
    }
  }

  isSlotInvalid(dayIndex: number, slotIndex: number): boolean {
    const slot = this.schedules[dayIndex]?.[slotIndex];
    if (!slot) return false;

    if (slot.open >= slot.close) return true;

    const daySlots = this.schedules[dayIndex] || [];
    for (let i = 0; i < daySlots.length; i++) {
      if (i === slotIndex) continue;
      const otherSlot = daySlots[i];
      if (this.slotsOverlap(slot, otherSlot)) return true;
    }

    return false;
  }

  private slotsOverlap(slot1: TimeSlot, slot2: TimeSlot): boolean {
    return slot1.open < slot2.close && slot2.open < slot1.close;
  }

  saveOpeningHours(): void {
    const estId = this.session.selectedId();
    if (!estId) return;
    if (!this.canSaveHours()) {
      this.showToast('Aucune modification à enregistrer');
      return;
    }

    const payload: ReplaceHoursPayload = { opening_hours: this.buildHoursPayload() };
    this.api.replaceOpeningHours(estId, payload).subscribe({
      next: () => {
        this.showToast('Horaires enregistrés');
        this.loadHours();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Erreur lors de l\'enregistrement des horaires';
        this.showToast(msg);
        console.error('[Horaires] replaceOpeningHours error');
      }
    });
  }

  private buildHoursPayload(): DayDto[] {
    const out: DayDto[] = [];
    for (let i = 0; i < 7; i++) {
      if (!this.daysEnabled[i]) continue;
      const slots = (this.schedules[i] || [])
        .map((s) => ({ start: this.toHHmm(s.open), end: this.toHHmm(s.close) }))
        .filter((s) => s.start && s.end)
        .sort((a, b) => a.start.localeCompare(b.start));
      if (slots.length) out.push({ weekday: i + 1, slots });
    }
    return out;
  }

  private toHHmm(v: string | null | undefined): string {
    if (!v) return '';
    const s = String(v).trim();
    const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!m) return '00:00';
    const hh = String(Math.min(23, Math.max(0, +m[1]))).padStart(2, '0');
    const mm = String(Math.min(59, Math.max(0, +m[2]))).padStart(2, '0');
    return `${hh}:${mm}`;
  }
}